// /resources/js/app/core/Toast.js

class ToastManager {
    constructor() {
        this.container = null;
        this._initContainer();
    }

    /**
     * Creates and appends the main toast container to the DOM.
     * This is done once to hold all subsequent toast notifications.
     */
    _initContainer() {
        if (document.getElementById('toast-container')) return;
        this.container = document.createElement('div');
        this.container.id = 'toast-container';
        // Positioned at the top-right, with z-index to be on top of everything.
        this.container.className = 'fixed top-4 right-4 z-[100] space-y-2';
        document.body.appendChild(this.container);
    }

    /**
     * Creates and displays a toast notification.
     * @param {string} message - The message to display. Can be plain text or HTML.
     * @param {string} [type='info'] - The type of toast ('success', 'error', 'info').
     * @param {number} [duration=5000] - How long the toast stays visible in ms. 0 for permanent.
     */
    show(message, type = 'info', duration = 5000) {
        const toast = document.createElement('div');
        
        // --- Determine colors based on type ---
        let bgColor, textColor, borderColor;
        switch (type) {
            case 'success':
                bgColor = 'bg-green-900/80';
                textColor = 'text-green-300';
                borderColor = 'border-green-700';
                break;
            case 'error':
                bgColor = 'bg-red-900/80';
                textColor = 'text-red-300';
                borderColor = 'border-red-700';
                break;
            default: // info
                bgColor = 'bg-blue-900/80';
                textColor = 'text-blue-300';
                borderColor = 'border-blue-700';
        }

        toast.className = `max-w-sm w-full ${bgColor} ${textColor} border ${borderColor} shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 overflow-hidden backdrop-blur-sm transition-all duration-300 ease-in-out transform translate-x-full opacity-0`;
        
        // --- Use innerHTML to allow for links/buttons ---
        toast.innerHTML = `
            <div class="p-4 text-sm">
                <div class="flex items-start">
                    <div class="flex-1">${message}</div>
                    <div class="ml-3 flex-shrink-0">
                        <button type="button" class="inline-flex rounded-md text-current opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white">
                            <span class="sr-only">Close</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        `;

        this.container.appendChild(toast);

        // --- Animate In ---
        requestAnimationFrame(() => {
            toast.classList.remove('translate-x-full', 'opacity-0');
        });
        // PASTE THIS NEW BLOCK
        const close = () => {
            toast.classList.add('opacity-0', 'translate-x-full');
            toast.addEventListener('transitionend', () => toast.remove());
        };

        // --- Auto-close or manual close ---
        // For errors, make them permanent until the user closes them (duration=0).
        const effectiveDuration = type === 'error' ? 0 : duration;

        if (effectiveDuration > 0) {
            setTimeout(close, effectiveDuration);
        }

        // Attach the close event to the new button.
        const closeButton = toast.querySelector('button');
        if (closeButton) {
            closeButton.addEventListener('click', (e) => {
                e.stopPropagation(); // Prevents the toast click listener from firing
                close();
            });
        }
    }
}

// Export a single, global instance for the entire application to use.
export const toast = new ToastManager();